import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { SessionStatus, type KioskSocketAuth } from '@print-saas/shared';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { PinDialog } from './components/PinDialog';
import { ResetPairingDialog } from './components/ResetPairingDialog';
import { ScreenTransition } from './components/ScreenTransition';
import { useDelayedFlag } from './hooks/useDelayedFlag';
import { useKioskSocket } from './hooks/useKioskSocket';
import { useBrandTheme } from './lib/branding';
import { ActiveScreen } from './screens/ActiveScreen';
import { AwaitingPaymentScreen } from './screens/AwaitingPaymentScreen';
import { CompletedScreen } from './screens/CompletedScreen';
import { FailedScreen } from './screens/FailedScreen';
import { PrintingScreen } from './screens/PrintingScreen';
import { SessionEndedScreen } from './screens/SessionEndedScreen';
import {
  AgentNeedsPairingScreen,
  CounterModeScreen,
  BootScreen,
  InvalidKeyScreen,
  NoPrinterScreen,
  PreviewExpiredScreen,
  PreviewIdleScreen,
  ReconnectingScreen,
  UnavailableScreen,
} from './screens/StatusScreens';
import { WaitingScreen } from './screens/WaitingScreen';

/** How long a dropped connection may last before we replace the UI with "Reconnecting…". */
const OFFLINE_GRACE_MS = 4_000;

interface KioskDisplayProps {
  apiUrl: string;
  /** A paired kiosk uses its key; the owner dashboard's read-only view uses a preview token. */
  credentials: { kioskKey: string } | { previewToken: string };
  onResetPairing: () => void;
}

export function KioskDisplay({ apiUrl, credentials, onResetPairing }: KioskDisplayProps) {
  const preview = 'previewToken' in credentials;
  const credential = preview ? credentials.previewToken : credentials.kioskKey;
  const auth = useMemo<KioskSocketAuth>(
    () => (preview ? { previewToken: credential, client: 'preview' } : { kioskKey: credential, client: 'display' }),
    [preview, credential],
  );
  const state = useKioskSocket(apiUrl, auth);
  useBrandTheme(state.config?.branding);
  const [pinOpen, setPinOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const offline = useDelayedFlag(
    state.connection === 'connecting' || state.connection === 'reconnecting',
    OFFLINE_GRACE_MS,
  );

  // Hidden logo gesture: re-pair this screen, behind the owner's kiosk PIN when one is set.
  const pinRequired = state.config?.pinRequired ?? false;
  const onSecret = useCallback(() => (pinRequired ? setPinOpen(true) : setResetOpen(true)), [pinRequired]);
  const closePin = useCallback(() => setPinOpen(false), []);
  const openReset = useCallback(() => setResetOpen(true), []);
  const closeReset = useCallback(() => setResetOpen(false), []);

  const { key, node } = pickScreen(state, offline, openReset, preview);

  return (
    <div className="flex h-full w-full flex-col">
      {preview && <PreviewBanner />}
      <Header config={state.config} apiUrl={apiUrl} onSecret={preview ? noop : onSecret} />
      <main className="relative min-h-0 flex-1">
        <ScreenTransition screenKey={key}>{node}</ScreenTransition>
      </main>
      <Footer
        kioskCode={state.config?.code ?? null}
        connection={state.connection}
        printer={state.config?.printer ?? null}
        screenOnline={preview ? (state.config?.displayOnline ?? null) : null}
      />
      <PinDialog
        open={pinOpen && !preview}
        title="Technician access"
        onSubmit={async (pin) => {
          await state.verifyPin(pin);
          setPinOpen(false);
          setResetOpen(true);
        }}
        onCancel={closePin}
      />
      <ResetPairingDialog
        open={resetOpen && !preview}
        kioskCode={state.config?.code ?? null}
        apiUrl={apiUrl}
        onCancel={closeReset}
        onConfirm={onResetPairing}
      />
    </div>
  );
}

const noop = () => {};

function PreviewBanner() {
  return (
    <div className="flex items-center justify-center gap-[1.2vmin] bg-info/15 px-[2vmin] py-[0.9vmin] text-[1.8vmin] font-semibold text-info ring-1 ring-info/30">
      <span className="size-[1.2vmin] animate-pulse rounded-full bg-info" />
      Live preview · read-only view of this kiosk's screen
    </div>
  );
}

function pickScreen(
  s: ReturnType<typeof useKioskSocket>,
  offline: boolean,
  onRepair: () => void,
  preview: boolean,
): { key: string; node: ReactNode } {
  if (s.connection === 'auth-error' && preview) {
    return { key: 'preview-expired', node: <PreviewExpiredScreen /> };
  }
  if (s.connection === 'auth-error') {
    return { key: 'auth-error', node: <InvalidKeyScreen message={s.authErrorMessage} onRepair={onRepair} /> };
  }

  const hasData = s.sessionLoaded || s.availability !== null;
  if (s.connection !== 'connected') {
    if (offline) return { key: 'offline', node: <ReconnectingScreen /> };
    // Within the grace period: keep showing whatever we had (or a neutral splash on boot).
    if (!hasData) return { key: 'boot', node: <BootScreen /> };
  }

  // The PC's agent was cut off by Rotate key: nothing below can work until it is paired again.
  if (s.config?.agentNeedsPairing) return { key: 'agent-needs-pairing', node: <AgentNeedsPairingScreen /> };
  // Made a shop counter in the portal: no QR codes on screen (customers scan its sticker).
  if (s.config?.mode === 'COUNTER') return { key: 'counter-mode', node: <CounterModeScreen /> };

  const unavailable = s.availability ? !s.availability.available : (s.config?.disabled ?? false);
  if (unavailable) {
    const reason = s.availability?.reason ?? (s.config?.disabled ? 'DISABLED' : null);
    if (reason === 'NO_PRINTER' && s.config) {
      return {
        key: 'no-printer',
        node: <NoPrinterScreen config={s.config} onSelect={s.selectPrinter} readOnly={preview} />,
      };
    }
    return { key: `unavailable-${reason ?? 'DISABLED'}`, node: <UnavailableScreen reason={reason} /> };
  }

  const session = s.session;
  // A preview with no real screen connected: nobody at the kiosk can see the QR, so don't show (or offer) it here.
  const screenOff = preview && s.config?.displayOnline === false && (!session || session.status === SessionStatus.WAITING);
  if (screenOff || (!session && preview && s.sessionLoaded)) {
    return { key: 'preview-idle', node: <PreviewIdleScreen /> };
  }
  if (!session) {
    return { key: 'preparing', node: <BootScreen label={s.sessionLoaded ? 'Getting ready…' : 'Loading…'} /> };
  }

  switch (session.status) {
    case SessionStatus.WAITING:
      return { key: 'waiting', node: <WaitingScreen session={session} config={s.config} /> };
    case SessionStatus.ACTIVE:
      return { key: `active-${session.id}`, node: <ActiveScreen session={session} /> };
    case SessionStatus.AWAITING_PAYMENT:
      return { key: `payment-${session.id}`, node: <AwaitingPaymentScreen session={session} /> };
    case SessionStatus.PAID:
    case SessionStatus.PRINTING:
      return { key: `printing-${session.id}`, node: <PrintingScreen session={session} /> };
    case SessionStatus.COMPLETED:
      return { key: `completed-${session.id}`, node: <CompletedScreen session={session} /> };
    case SessionStatus.FAILED:
      return { key: `failed-${session.id}`, node: <FailedScreen session={session} /> };
    case SessionStatus.EXPIRED:
    case SessionStatus.CANCELLED:
      return { key: `ended-${session.id}`, node: <SessionEndedScreen session={session} /> };
  }
}
